/**
 * Cuánto le ha costado al encuestador, en datos de su plan, usar la aplicación.
 *
 * Es SÓLO INFORMATIVO: se muestra pero no se puede ajustar desde aquí.
 *
 * La clasificación entre datos móviles y WiFi la decide el servidor por el
 * proveedor desde el que entra la petición. No hay interruptor manual a
 * propósito: esta cifra alimenta un reembolso, y permitir que quien lo cobra
 * la modifique sería ponerle a decidir sobre su propio pago. Las correcciones
 * se hacen desde el panel de coordinación.
 */

import { useEffect, useState } from 'react';

import { formatBytes, type ConnectionKind } from '@capi/shared';
import { api } from '../api/client';
import { effectiveConnection, getContext, pendingTotals, subscribe } from '../telemetry/dataUsage';

export function DataUsageCard({ isOnline }: { isOnline: boolean }) {
  const [servidor, setServidor] = useState<{ movil: number } | null>(null);
  const [pendiente, setPendiente] = useState({ movil: 0, wifi: 0 });
  const [, forzar] = useState(0);

  useEffect(() => subscribe(() => forzar((n) => n + 1)), []);

  useEffect(() => {
    void pendingTotals().then(setPendiente);
    if (!isOnline) return;
    void api
      .myDataUsage()
      .then((r) => setServidor({ movil: r.movil }))
      .catch(() => undefined);
  }, [isOnline]);

  const { connection } = effectiveConnection();
  const contexto = getContext();

  /**
   * Acumulado histórico: lo que ya está en el servidor más lo que todavía no
   * se ha enviado. Sin sumar lo pendiente, el contador BAJARÍA al sincronizar
   * y parecería que se perdió consumo.
   */
  const totalMovil = (servidor?.movil ?? 0) + pendiente.movil;

  /**
   * Una red LOCAL no es una red sin identificar.
   *
   * Contra un servidor de desarrollo en localhost nunca habrá proveedor que
   * resolver, y decir «red sin identificar» hacía parecer que la detección
   * fallaba cuando funcionaba exactamente como debe. En campo, en cambio, un
   * proveedor sin resolver sí es algo que hay que atender.
   */
  const etiqueta: Record<ConnectionKind, string> = {
    movil: 'Datos móviles',
    wifi: 'WiFi',
    desconocida: contexto.privateNetwork ? 'Red local' : 'Identificando red…',
  };

  return (
    <div className="usage">
      <div className="usage__head">
        <span className="eyebrow">Datos móviles usados</span>
        <span className={`usage__state usage__state--${connection}`}>
          {etiqueta[connection]}
          {contexto.ispName ? ` · ${contexto.ispName}` : ''}
        </span>
      </div>

      <div className="usage__value">{formatBytes(totalMovil)}</div>
      <div className="usage__meta">
        Acumulado desde que empezaste
        {pendiente.movil > 0 ? ` · ${formatBytes(pendiente.movil)} sin enviar` : ''}
      </div>

      <p className="usage__note">
        Sólo se cuenta lo que gastas con datos móviles. Lo que uses en WiFi no suma.
      </p>
    </div>
  );
}
